import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { ALL_URL_KEYS } from "@_constants/url";
import type {
  VISUALISATION_TYPE,
  MODE_TYPES,
  BASES_TYPES,
  PERIOD_TYPES,
} from "./screenConfig.constants";
import type { StatTestingOptionEntry } from "./moduleConfig.types";

type URL_KEYS_TYPES = (typeof ALL_URL_KEYS)[number];

// ─── Schema ──────────────────────────────────────────────────────────────────

/**
 * Describes the full feature-capability contract for a route, module, or
 * screen-type configuration entry. Each property acts as a declarative flag
 * or default value that drives which UI controls are rendered and how the
 * data-fetching layer is parameterised.
 *
 * `type` is the only required field; all other fields are optional so that
 * configs can be incrementally composed.
 */
export interface CONFIG_SCHEMA {
  /**
   * Classifies what level of the routing hierarchy this config entry represents:
   * - MODULE             – A top-level feature module (e.g. Visibility, Reputation)
   * - SCREEN_TYPE        – A sub-screen within a module (e.g. By Brand, By Market)
   * - MODE               – A specific analytical mode within a screen
   * - VISUALIZATION_TYPE – A specific visualisation variant within a mode
   */
  type: "MODULE" | "SCREEN_TYPE" | "MODE" | "VISUALIZATION_TYPE";

  /** Defines whether the segment modal allows applying filters or is restricted to segment-only selection. */
  segmentFilterModalMode?: "SEGMENT_WITH_FILTERS" | "SEGMENT_ONLY";

  module?: string;
  /** Whether the markets selector is available on this screen. */
  supportMarkets?: boolean;
  /** When true, the markets list is narrowed to only those relevant to the currently selected brand. */
  isMarketsFilteredByBrand?: boolean;
  /** The maximum number of markets that may be selected before the screen can fetch data. */
  maximumAllowedMarkets?: number;
  /** The minimum number of markets that must be selected before the screen can fetch data. */
  minimumRequiredMarkets?: number;

  /** Whether the primary brands selector is available on this screen. */
  supportBrands?: boolean;
  /** When true, the brands list is narrowed to only those available in the currently selected market. */
  isBrandsFilteredByMarket?: boolean;
  /**
   * When true, the brands list is narrowed to only those whose `reputationMarketIds` array
   * overlaps with **any** of the currently selected markets.
   * Mirrors the eligibility check in `useReputationPayloadOverride.ts` and is applied inside
   * `buildActiveGroupSnapshot` to produce a reputation-aware `marketFilteredBrandIds` pool.
   * Takes precedence over `isBrandsFilteredByMarket` when both are set.
   */
  isBrandsFilteredByReputationMarket?: boolean;
  /**
   * When true, the brands list is narrowed to only those whose `reputationMarketIds` array
   * overlaps with the brand's **own** `marketIds` array (i.e. the brand has reputation data
   * for at least one of the markets it belongs to).
   *
   * Used for the Reputation By Market screen where the brand is the primary dimension and
   * markets are derived from the selected brand — there are no pre-selected markets to check
   * against, so `isBrandsFilteredByReputationMarket` cannot be used.
   *
   * Takes precedence over `isBrandsFilteredByMarket` when both are set.
   * Evaluated after `isBrandsFilteredByReputationMarket` in the priority chain.
   */
  isBrandsFilteredByOwnReputationMarkets?: boolean;
  /** The maximum number of primary brands that may be selected before the screen can fetch data. */
  maximumAllowedBrands?: number;
  /** The minimum number of primary brands that must be selected before the screen can fetch data. */
  minimumRequiredBrands?: number;
  /** Backend-driven cap on members in a single Brand Set. Falls back to the hook's MAX_BRANDS default. */
  brandSetMaxMembers?: number;

  /** Whether the secondary (competitor) brands selector is available on this screen. */
  supportSecondaryBrands?: boolean;
  /** When true, the secondary brands list is narrowed to only those available in the currently selected market. */
  isSecondaryBrandsFilteredByMarket?: boolean;
  /** The maximum number of secondary brands that may be selected before the screen can fetch data. */
  maximumAllowedSecondaryBrands?: number;
  /** The minimum number of secondary brands that must be selected before the screen can fetch data. */
  minimumRequiredSecondaryBrands?: number;

  /** Whether the user can switch between different chart/visualisation types on this screen. */
  supportVisualisationSwitch?: boolean;
  /** The subset of visualisation types that are valid choices for this screen. */
  supportedVisualisationTypes?: VISUALISATION_TYPE[];
  /** The visualisation type that is pre-selected when the screen first loads. */
  defaultVisualisationType?: VISUALISATION_TYPE;

  /** Whether a primary date-period selector is shown on this screen. */
  supportDatePeriod?: boolean;
  /** The date-period value that is pre-selected when the screen first loads. */
  defaultDatePeriod?: string;
  /**
   * Flat date boundaries for the primary date picker, placed at the config layer where
   * they apply (module, mode, or visualisationType).  The config-hierarchy resolution
   * automatically overrides shallower layers with deeper ones — no `byMode` wrapper needed.
   *
   * `warningMessage` is shown in the UI when the user's YOY/POP prior-period extends
   * before `min` (surfaced by `displayWarnigText` in `utils/common.ts`).
   *
   * `bySubKey` is an **authoring-only** field — `resolveModuleConfig` applies the
   * per-profile-metric override (keyed by the 5th `profileMetric` argument) and strips
   * `bySubKey` from the resolved output.  Consumers always receive a fully-flat
   * `{ min?, max?, warningMessage? }` shape with no `bySubKey` present at runtime.
   *
   * @example
   * // Advertising TREND mode config (authoring)
   * dateBounds: { min: "2022-09-21", max: null, warningMessage: "Data available from Sep 21, 2022." }
   *
   * @example
   * // Advertising PROFILE vizType config — profile-metric 5 has its own cutoff (authoring)
   * dateBounds: { bySubKey: { "5": { min: "2022-09-21", max: null } } }
   */
  dateBounds?: {
    /** Earliest selectable date (ISO 8601 string). */
    min?: string | null;
    /** Latest selectable date (ISO 8601 string). */
    max?: string | null;
    /**
     * Warning shown when the YOY or POP prior-period extends before `min`.
     * @example "Data for the previous time period comparison is only available from September 21, 2022."
     */
    warningMessage?: string;
    /**
     * Authoring-only: per-profile-metric overrides (PROFILE vizType only).
     * Keyed by the string representation of the profile-metric id.
     * Resolved and stripped by `resolveModuleConfig` — never present in the output.
     */
    bySubKey?: Record<
      string,
      { min?: string | null; max?: string | null; warningMessage?: string }
    >;
  };

  /** Whether a secondary "post" date-period selector is shown (used in Pre/Post mode). */
  supportPostDatePeriod?: boolean;
  /** The post-period value that is pre-selected when the screen first loads. */
  defaultPostPeriod?: string;
  /**
   * Flat date boundaries for the post-period date picker (Pre/Post mode only).
   * Same shape as `dateBounds` — placed at the PRE_POST mode config layer.
   * `bySubKey` is authoring-only — resolved and stripped by `resolveModuleConfig`.
   */
  postDateBounds?: {
    /** Earliest selectable post-period date (ISO 8601 string). */
    min?: string | null;
    /** Latest selectable post-period date (ISO 8601 string). */
    max?: string | null;
    /**
     * Warning shown when the YOY or POP prior-period extends before `min`.
     */
    warningMessage?: string;
    /**
     * Authoring-only: per-profile-metric overrides (PROFILE vizType only).
     * Keyed by the string representation of the profile-metric id.
     * Resolved and stripped by `resolveModuleConfig` — never present in the output.
     */
    bySubKey?: Record<string, { min?: string | null; max?: string | null }>;
  };

  /** Whether the analytical mode selector (Trend / Point-in-Time / Pre-Post) is shown on this screen. */
  supportMode?: boolean;
  /** The subset of analytical modes that are valid choices for this screen. */
  supportedModes?: MODE_TYPES[];
  /** The analytical mode that is pre-selected when the screen first loads. */
  defaultMode?: MODE_TYPES;

  /** Whether the respondent-base selector is shown on this screen. */
  supportBase?: boolean;
  /** The subset of respondent bases that are valid choices for this screen. */
  supportedBases?: BASES_TYPES[];
  /** The respondent base that is pre-selected when the screen first loads. */
  defaultBase?: BASES_TYPES;

  /** Whether the rolling/fixed time-period selector is shown on this screen. */
  supportPeriod?: boolean;
  /** The subset of time-period granularities that are valid choices for this screen. */
  supportedPeriod?: PERIOD_TYPES[];
  /** The time-period granularity that is pre-selected when the screen first loads. */
  defaultPeriod?: PERIOD_TYPES;

  /**
   * Ordered array of stat-testing option configurations for this screen.
   * Array order determines the render order in the StatTesting dropdown,
   * making it easy to add new options in future without changing component logic.
   *
   * - When this field is **absent** or an **empty array**, stat testing is
   *   completely hidden for this screen.
   * - Each entry declares visibility (`hidden`), enabled state, disabled
   *   messages, runtime disable rules (date-range, period) and a fallback
   *   option id.
   *
   * Replaces both the legacy `supportStatTesting` and `supportedStatTesting`
   * fields — presence of a non-empty array implies stat testing is supported.
   */
  statTestingConfig?: StatTestingOptionEntry[];

  /** Whether the trends metric selector is shown on this screen. */
  supportTrendsMetric?: boolean;
  /** The trends metric that is pre-selected when the screen first loads. */
  defaultTrendsMetric?: string;

  /** The subset of URL query-parameter keys that this screen reads from and writes to the browser URL. */
  supportedURLKeys?: URL_KEYS_TYPES[];

  // ─── API contract (analyticsUnified) ─────────────────────────────────────

  /**
   * The list of metric keys sent to `analytics/unified` for this module.
   * Defined once at the MODULE level and inherited by all screen types and modes.
   *
   * @example ["equity", "momentum", "consideration", "quality", "familiarity"]
   */
  apiMetricKeys?: string[];

  /**
   * The `groupedBy` discriminator sent to `analytics/unified`.
   * Determines which entity array (brands / markets / segments) is used as the
   * primary dimension, and therefore which formatter variant to call.
   *
   * Set at SCREEN_TYPE level (BRAND / MARKET / SEGMENT).
   */
  apiGroupedBy?: "BRAND" | "MARKET" | "SEGMENT";

  /**
   * The `viewMode` discriminator sent to `analytics/unified`.
   * Set at MODE level.
   *
   * @example "TREND" | "POINT_IN_TIME" | "PRE_POST"
   */
  apiViewMode?: "TREND" | "POINT_IN_TIME" | "PRE_POST";

  /**
   * When `true`, the `filters` array (built from `userSelection.filters` +
   * `ageRange`) is included in the unified payload.
   * Typically `true` for BRAND and MARKET screen types, `false` for SEGMENT.
   */
  apiSendFilters?: boolean;

  /**
   * When `true`, the `intervalType` field (= `userSelection.period`) is
   * included in the unified payload.  Only required by TREND mode.
   */
  apiSendIntervalType?: boolean;

  /**
   * When `true`, the `prePeriod` block is included in the unified payload
   * alongside the main `period` block.  Only required by PRE_POST mode.
   */
  apiSendPrePeriod?: boolean;

  /**
   * When `true`, the `scoreType` field is included inside each entry of the
   * `metricModules` array in the unified payload.
   * Set to `true` for the Reputation module, which exposes score-type selection.
   */
  apiSendScoreType?: boolean;

  /**
   * When `true`, the `competitors` array (mapped from `userSelection.secondaryBrands`)
   * is included in the V2 analytics payload.
   *
   * Set to `true` for BUTTERFLY, PERCEPTUAL, and DIFFERENTIATION visualization-type
   * config entries, where a secondary (opponent) brand selection is required by the
   * dedicated endpoints.
   */
  apiSendCompetitors?: boolean;

  /**
   * When `true`, the first entity in `entities` is kept as-is and the
   * remaining entities are moved to `competitors` in the V2 request payload.
   *
   * Used by the PERCEPTUAL visualization type, where all selected brands are
   * passed as primary entities but the API expects:
   *   - `entities[0]`   → the focal/anchor brand
   *   - `competitors`   → all other selected brands
   */
  apiSendRestEntitiesAsCompetitors?: boolean;
}

// ─── State ───────────────────────────────────────────────────────────────────

/**
 * Keyed map of screen/module identifiers → their `CONFIG_SCHEMA`.
 * Each route can register and update its own config independently.
 */
export type ScreenConfigState = CONFIG_SCHEMA;

const initialState: ScreenConfigState | {} = {};

// ─── Slice ───────────────────────────────────────────────────────────────────

export const screenConfigSlice = createSlice({
  name: "screenConfig",
  initialState,
  reducers: {
    /**
     * Register or fully replace the config for a given screen key.
     *
     * @example
     * dispatch(setScreenConfig({ key: "visibility/byBrand", config: { type: "SCREEN_TYPE", supportBrands: true } }))
     */
    setScreenConfig: (state, action: PayloadAction<CONFIG_SCHEMA>) => {
      return action.payload;
    },

    /**
     * Merge a partial update into an existing screen config entry.
     * Only the supplied fields are overwritten; all other fields are preserved.
     * If the key does not yet exist this action is a no-op — use
     * `setScreenConfig` to create new entries.
     *
     * @example
     * dispatch(updateScreenConfig({ key: "visibility/byBrand", patch: { defaultMode: "TREND", supportBase: false } }))
     */
    updateScreenConfig: (
      state,
      action: PayloadAction<Partial<CONFIG_SCHEMA>>,
    ) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    /**
     * Reset the entire screen-config registry to its initial (empty) state.
     */
    resetScreenConfig: () => initialState,
  },
});

export const { setScreenConfig, updateScreenConfig, resetScreenConfig } =
  screenConfigSlice.actions;

export default screenConfigSlice.reducer;
